import { useEffect, useRef, useState } from "react";
import { Check, PencilSimple, X } from "@phosphor-icons/react";

/**
 * Rename a storage location in place.
 *
 * A dialog for one text field is heavier than the edit deserves, and this
 * codebase's other lightweight prompt — `window.prompt` for a void reason —
 * hides what the current value is at the moment you are asked to change it.
 * Editing where the name already sits keeps the old value in front of you and
 * the tree around it visible.
 *
 * Escape cancels and Enter commits, because a name field that can only be
 * dismissed by finding a small button is a trap for anyone working by
 * keyboard. The field reverts to the last saved value on cancel rather than
 * keeping the draft, so an abandoned edit leaves nothing behind.
 */
export default function RenameField({
  value,
  onRename,
  label,
  className = "",
  testid,
  children,
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value);
  const [saving, setSaving] = useState(false);
  const inputRef = useRef(null);

  useEffect(() => {
    if (editing) inputRef.current?.focus();
  }, [editing]);

  const start = () => {
    setDraft(value);
    setEditing(true);
  };

  const cancel = () => {
    setDraft(value);
    setEditing(false);
  };

  const commit = async () => {
    const next = draft.trim();
    if (!next || next === value) {
      cancel();
      return;
    }
    setSaving(true);
    try {
      await onRename(next);
      setEditing(false);
    } catch {
      // Swallowed, not ignored: the caller raises the toast and rethrows so
      // this stays open with the typed name intact, ready to correct rather
      // than retype. Letting the rejection past a click handler would only
      // become an unhandled promise.
    } finally {
      setSaving(false);
    }
  };

  if (!editing) {
    return (
      <div className={`flex min-w-0 items-center gap-1.5 ${className}`}>
        {children ?? <span className="truncate">{value}</span>}
        {onRename && (
          <button
            type="button"
            onClick={start}
            data-testid={testid ? `${testid}-edit` : undefined}
            title={`Rename ${label}`}
            aria-label={`Rename ${label}`}
            className="shrink-0 rounded p-1 text-slate-300 transition-colors hover:bg-slate-100 hover:text-slate-700"
          >
            <PencilSimple size={13} />
          </button>
        )}
      </div>
    );
  }

  return (
    <div className={`flex min-w-0 items-center gap-1 ${className}`}>
      <input
        ref={inputRef}
        value={draft}
        disabled={saving}
        onChange={(event) => setDraft(event.target.value)}
        onKeyDown={(event) => {
          if (event.key === "Enter") {
            event.preventDefault();
            commit();
          }
          if (event.key === "Escape") {
            event.preventDefault();
            cancel();
          }
        }}
        aria-label={`New name for ${label}`}
        data-testid={testid ? `${testid}-input` : undefined}
        className="min-w-0 flex-1 rounded border border-slate-300 px-2 py-1 text-sm text-ink focus:border-transparent focus:outline-none focus:ring-2 focus:ring-teal-600"
      />
      <button
        type="button"
        onClick={commit}
        disabled={saving}
        data-testid={testid ? `${testid}-save` : undefined}
        title="Save name"
        aria-label="Save name"
        className="shrink-0 rounded p-1 text-teal-700 transition-colors hover:bg-teal-50 disabled:opacity-50"
      >
        <Check size={14} />
      </button>
      <button
        type="button"
        onClick={cancel}
        disabled={saving}
        data-testid={testid ? `${testid}-cancel` : undefined}
        title="Cancel rename"
        aria-label="Cancel rename"
        className="shrink-0 rounded p-1 text-slate-400 transition-colors hover:bg-slate-100 disabled:opacity-50"
      >
        <X size={14} />
      </button>
    </div>
  );
}
